import { Skeleton } from '@tempo-io/sui-skeleton';
import { useTranslation } from 'react-i18next';
import { Row, Section } from './financialPanelLayout';
import { SavableUnitField } from './SavableUnitField';
import type { FinancialSaveState, ItemFinancialFigures } from './types';
import { MAX_MONETARY_AMOUNT } from './types';
import { useLiveDraft } from './useLiveDraft';

interface RevenueSectionProps {
  figures?: ItemFinancialFigures;
  symbol: string;
  money: (value: number | null | undefined) => string;
  isReadOnly: boolean;
  isLoading?: boolean;
  onForecastedRevenueChange?: (value: number | null) => void;
  saveState?: FinancialSaveState;
  showHeaderValue?: boolean;
  defaultOpen?: boolean;
}

export const RevenueSection = ({
  figures,
  symbol,
  money,
  isReadOnly,
  isLoading = false,
  onForecastedRevenueChange,
  saveState = { status: 'idle' },
  showHeaderValue = false,
  defaultOpen = true,
}: RevenueSectionProps) => {
  const { t } = useTranslation();
  const forecastedRevenue = figures?.forecastedRevenue ?? null;
  const [liveForecastedRevenue, setLiveForecastedRevenue] = useLiveDraft(forecastedRevenue);
  const canEditRevenue = figures?.hierarchyRootSource === 'SELF';
  const isRevenueInherited = figures?.hierarchyRootSource === 'PARENT';

  if (figures == null) {
    return null;
  }

  const revenueSublabel = isRevenueInherited
    ? t(($) => $.financials.panel.revenueInheritedFromParent)
    : undefined;

  return (
    <Section
      title={t(($) => $.financials.panel.revenueSectionTitle)}
      subtitle={t(($) => $.financials.panel.revenueSectionSubtitle)}
      headerValue={showHeaderValue ? money(liveForecastedRevenue) : undefined}
      defaultOpen={defaultOpen}
    >
      <Row
        label={t(($) => $.financials.panel.forecastedRevenue)}
        sublabel={revenueSublabel}
        right={
          isLoading ? (
            <Skeleton className="h-9 w-36" data-testid="financial-revenue-skeleton" />
          ) : isReadOnly || !onForecastedRevenueChange || !canEditRevenue ? (
            money(figures.forecastedRevenue)
          ) : (
            <SavableUnitField
              saveState={saveState}
              value={forecastedRevenue}
              unit={symbol}
              unitSide="left"
              ariaLabel={t(($) => $.financials.panel.forecastedRevenue)}
              decimals={2}
              max={MAX_MONETARY_AMOUNT}
              maxDecimals={2}
              onCommit={onForecastedRevenueChange}
              onChange={setLiveForecastedRevenue}
              testId="financial-revenue-input"
            />
          )
        }
      />
    </Section>
  );
};
